import { format } from "date-fns";
import React, { Fragment, useContext, useEffect, useState } from "react"
import { formatOptionalJSONDateWithoutTime, parseJSONDateWithoutTime } from "../../utils/dateHelpers";
import getclassNames from "../../utils/getClassNames";
import Button from "../UI/Button";
import IconButton from "../UI/IconButton";
import Loader from "../UI/Loader";
import "./SearchResultsTable.css";
import { createFormInstance, getClientFormInstance, getLatestForm } from "../../services/formsService";
import FormViewer from "../UI/FormViewer";
import Client, { AssignedUser } from "../../models/serviceModels/Client";
import HouseholdMember from "../../models/serviceModels/HouseholdMember";
import { assignClientsToUsers, unAssignClientsFromUsers, getClientById } from "../../services/clientsService";
import { Link } from "react-router-dom";
import Dropdown from "../UI/Dropdown";
import FormEditor from "../Form/Editor/FormEditor";
import Form from "../../models/serviceModels/Form";
import EditableFormField from "../../models/pageModels/EditableFormField";
import { mapFormFieldToEditableFormField, normalizeFormInstanceFieldValue } from "../../mappers/formFieldMapper";
import ApiErrorMessages from "../UI/ApiErrorMessages";
import { UPLOADED } from "../../constants/fileUploadStatus";
import toast from "react-hot-toast";
import { ClientResultsContext } from "./ParticipantSearch";
import Modal from "../UI/Modal";
import User from "../../models/serviceModels/User";
import { searchUsers } from "../../services/userService";
import Select from "../UI/Select";
import { MeContext, OrganizationContext } from "../../App";
import { ADMIN_ROLE_ID, CONTRIBUTOR_ROLE_ID, READONLY_ROLE_ID } from "../../constants/userRoles";
import { assignedUserEnabled, householdIdentifiersEnabled, linkedHouseholdParticipantsEnabled, manageProgramsV2Enabled } from "../../utils/features";
import { getDisplayGenderName, getDisplayRaceNames, getEthnicityFieldVisible, getRaceFieldLabel, isDemographicVisible } from "../../utils/demographicsSchema";
import ExpandableDataTable from "../UI/ExpandableDataTable";
import { DataTableColumn } from "../../models/DataTable";
import Checkbox from "../UI/Checkbox";
import SearchResultsTableV1 from "./SearchResultsTableV1";

interface SelectedForm {
    client: Client;
    formId: string;
    formName: string;
}

interface SelectedClients {
    id: string;
    assignedTo: AssignedUser | null;
    isAssigned: boolean;
}

const SearchResultsTableV2 = () => {
    const [loadingClientForms, setLoadingClientForms] = useState<any[]>([]);
    const [loadingClients, setLoadingClients] = useState<string[]>([]);
    const [clientForms, setClientForms] = useState<any[]>([]);
    const [loadedClients, setLoadedClients] = useState<Client[]>([]);
    const [selectedForm, setSelectedForm] = useState<SelectedForm>();
    const [loadingSelectedForm, setLoadingSelectedForm] = useState(false);
    const [loadedForm, setLoadedForm] = useState<Form>();
    const [loadedFormFields, setLoadedFormFields] = useState<EditableFormField[]>([]);
    const [apiError, setApiError] = useState<any>();
    const [submitting, setSubmitting] = useState(false);
    const ctx = useContext(ClientResultsContext);
    if (!ctx) {
        throw new Error("ClientResultsContext is missing provider");
    }
    const { clients, refresh, isFetching = false, sortBy = '', sortDirection = 'asc', onSort = () => undefined } = ctx;
    const [isUserAssignmentModalOpen, setIsUserAssignmentModalOpen] = useState(false);
    const [isUserUnassignmentModalOpen, setIsUserUnassignmentModalOpen] = useState(false);
    const [isUsersLoading, setIsUsersLoading] = useState(false);
    const [isAssigning, setIsAssigning] = useState(false);
    const [isUnassigning, setIsUnassigning] = useState(false);
    const [assignableUsers, setAssignableUsers] = useState<User[]>([]);
    const [selectedClients, setSelectedClients] = useState<SelectedClients[]>([]);
    const [selectedUser, setSelectedUser] = useState<string>();
    const me = useContext(MeContext);
    const organization = useContext(OrganizationContext);
    const showGender = isDemographicVisible(organization.value, 'gender');
    const showEthnicity = getEthnicityFieldVisible(organization.value);
    const showRace = isDemographicVisible(organization.value, 'race');
    const showPhone = isDemographicVisible(organization.value, 'phone');
    const raceFieldLabel = getRaceFieldLabel(organization.value);

    const isReadOnly = me.user?.userRoleId === READONLY_ROLE_ID;
    const canEditParticipants = me.user?.userRoleId === CONTRIBUTOR_ROLE_ID || me.user?.userRoleId === ADMIN_ROLE_ID;
    const showTrackData = canEditParticipants;
    const showCheckbox = !selectedForm && assignedUserEnabled;

    const getColumns = (): DataTableColumn[] => {
        const cols: DataTableColumn[] = [];
        if (showCheckbox) cols.push({ key: 'checkbox', label: '' });
        cols.push({ key: 'expand', label: '' });
        cols.push({ key: 'fullName', label: 'name', sortable: true });
        if (householdIdentifiersEnabled) cols.push({ key: 'householdId', label: 'household id' });
        cols.push({ key: 'dateOfBirth', label: 'birthdate', sortable: true });
        if (showPhone) cols.push({ key: 'phoneNumber', label: 'phone number', sortable: true });
        cols.push({ key: 'updated', label: 'updated', sortable: true });
        if (assignedUserEnabled) cols.push({ key: 'assignedUser', label: 'assigned user', sortable: true });
        if (showTrackData) cols.push({ key: 'trackData', label: 'track data' });
        return cols;
    };

    useEffect(() => {
        closeForm();
        setSelectedClients([]);
        setSelectedUser(undefined);
    }, [clients]);

    useEffect(() => {
        if (!isUserAssignmentModalOpen || assignableUsers.length > 0) {
            return;
        }

        const fetchUsers = async () => {
            setIsUsersLoading(true);
            setApiError(undefined);

            try {
                const users = await searchUsers("", 0, 10000);
                users.data = users.data.filter(u => !u.deactivated);
                setAssignableUsers(users.data);
            } catch (e) {
                console.error(e);
                setApiError(e);
            } finally {
                setIsUsersLoading(false);
            }
        }

        fetchUsers();
    }, [isUserAssignmentModalOpen, assignableUsers.length]);

    const handleLoadingClient = (client: Client) => {
        setLoadingClients(prev => [...prev, client.id]);

        getClientById({ id: client.id }).then(c => {
            setLoadedClients(prev => [...prev, c]);
        }).catch(e => {
            console.error(e);
        }).finally(() => {
            setLoadingClients(prev => prev.filter(lc => lc !== client.id));
        });
    };

    const handleLoadingClientForm = (client: Client) => {
        setLoadingClientForms(prev => [...prev, client]);

        getClientFormInstance(client.id).then(formInstance => {
            if (formInstance) {
                setClientForms(prev => [...prev, { clientId: client.id, ...formInstance }]);
            }
        }).catch(e => {
            console.error(e);
        }).finally(() => {
            setLoadingClientForms(prev => prev.filter(lcf => lcf !== client));
        });
    };

    const handleOnExpand = (client: Client) => {
        if (!loadedClients.find(c => c.id === client.id)) handleLoadingClient(client);
        if (!clientForms.find(cf => cf.clientId === client.id)) handleLoadingClientForm(client);
    };

    const openForm = (form: SelectedForm) => {
        setSelectedForm(form);
        setLoadingSelectedForm(true);

        getLatestForm(form.formId, true).then(f => {
            setLoadedForm(f);
            setLoadedFormFields(f.fields.map(ff => mapFormFieldToEditableFormField(ff)));
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setLoadingSelectedForm(false);
        });
    };

    const closeForm = () => {
        setSelectedForm(undefined);
        setApiError(undefined);
        setLoadingSelectedForm(false);
        setLoadedForm(undefined);
        setLoadedFormFields([]);
    };

    const getClientForm = (clientId: string) => clientForms.find(cf => cf.clientId === clientId);
    const getLoadedClient = (clientId: string) => loadedClients.find(lc => lc.id === clientId);

    const getDisplayHouseholdMembers = (client?: Client) => client?.householdMembers || [];

    const getDisplayHouseholdIds = (client?: Client) => {
        if (!client) return '';
        if (client.householdIds) return client.householdIds;

        const householdIds = [
            client.householdId,
            ...(client.linkedHouseholds || []).map(household => household.owner.householdId)
        ]
            .filter((householdId): householdId is number => householdId !== undefined && householdId !== null)
            .map(String);

        return householdIds.filter((householdId, index) => householdIds.indexOf(householdId) === index).join(', ');
    };

    const handleSubmitForm = (e: React.FormEvent) => {
        e.preventDefault();
        setApiError(undefined);

        if (!loadedForm?.id || !selectedForm) {
            console.warn('No loaded form or selected form');
            return;
        }

        setSubmitting(true);

        createFormInstance({
            formId: loadedForm.id,
            versionId: loadedForm.versionId,
            clientId: selectedForm.client.id,
            fields: loadedFormFields.map(f => ({
                fieldId: f.id,
                value: normalizeFormInstanceFieldValue(f, f.value),
                files: f.files?.map(f => f.fileId!) || []
            }))
        }).then(() => {
            toast.success("Successfully submitted form");
            closeForm();
        }).catch(e => {
            setApiError(e);
            console.error(e);
            toast.error("Failed to submit form");
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const handleOnFieldsChange = (fields: EditableFormField[]) => {
        setLoadedFormFields([...fields]);
    };

    const handleFormReset = () => {
        setLoadedFormFields(loadedFormFields.map(f => ({
            ...f,
            value: normalizeFormInstanceFieldValue(f),
            files: []
        })));
    };

    const getRecentForms = (client: Client) => {
        const options = [];
        if (client.recentFormId1 && client.recentFormName1) {
            options.push({ label: client.recentFormName1, onClick: () => openForm({ formId: client.recentFormId1!, formName: client.recentFormName1!, client }) });
        }
        if (client.recentFormId2 && client.recentFormName2) {
            options.push({ label: client.recentFormName2, onClick: () => openForm({ formId: client.recentFormId2!, formName: client.recentFormName2!, client }) });
        }
        if (client.recentFormId3 && client.recentFormName3) {
            options.push({ label: client.recentFormName3, onClick: () => openForm({ formId: client.recentFormId3!, formName: client.recentFormName3!, client }) });
        }
        return options;
    };

    const handleBulkAssignClientsToUsers = async () => {
        if (!selectedUser || selectedClients.length === 0) {
            console.error("No assignee or clients selected.");
            return;
        }

        if (selectedClients.some(c => c.isAssigned)) {
            toast.error("Some selected clients are already assigned to a user.");
            return;
        }

        setIsAssigning(true);
        setApiError(undefined);

        try {
            const clientIds = selectedClients.map(client => client.id);
            await assignClientsToUsers(clientIds, selectedUser);
            toast.success(`Successfully assigned ${selectedClients.length} participant(s) to user.`);
            setIsUserAssignmentModalOpen(false);
            setSelectedClients([]);
            setSelectedUser(undefined);
        } catch (e) {
            console.error(e);
            setApiError(e);
            toast.error("Failed to assign participants.");
        } finally {
            setIsAssigning(false);
            refresh();
        }
    };

    const handleBulkUnassignClientsFromUsers = async () => {
        if (selectedClients.length === 0) {
            console.error("No clients selected.");
            return;
        }

        if (!selectedClients.some(c => c.isAssigned)) {
            toast.error("Selected clients are not assigned to any user.");
            return;
        }

        setIsUnassigning(true);
        setApiError(undefined);

        try {
            const clients = selectedClients
                .filter(c => c.isAssigned && c.assignedTo)
                .map(c => ({ clientId: c.id, userId: c.assignedTo!.id }));

            await unAssignClientsFromUsers(clients);
            toast.success(`Successfully unassigned ${selectedClients.length} participant(s) from user.`);
            setIsUserUnassignmentModalOpen(false);
            setSelectedClients([]);
            setSelectedUser(undefined);
        } catch (e) {
            console.error(e);
            setApiError(e);
            toast.error("Failed to unassign participants.");
        } finally {
            setIsUnassigning(false);
            refresh();
        }
    };

    return (
        <div className='search-results-container'>
            {clients &&
                <>
                    <hr />
                    <div className="row justify-content-center">
                        <div className="column search-results-outer-column">
                            <div className="row search-results-content-row">
                                <div className="column search-results-table-column">
                                    <h2 className="text-center">Participant Search Results</h2>
                                    {!selectedForm && !isReadOnly && assignedUserEnabled &&
                                        <div className="row justify-content-between align-items-center margin-bottom-2 rounded padding-horizontal-3">
                                            <BulkActionAssignment
                                                selectedClients={selectedClients}
                                                setAssignmentModelOpen={setIsUserAssignmentModalOpen}
                                                setUnassignmentModelOpen={setIsUserUnassignmentModalOpen}
                                            />
                                        </div>
                                    }
                                    <div className="participant-search-results-shell">
                                        <div className="participant-search-results-table" tabIndex={0} aria-label="Participant search results">
                                            <ExpandableDataTable
                                                columns={getColumns()}
                                                data={clients}
                                                sortBy={sortBy}
                                                sortDirection={sortDirection}
                                                onSort={onSort}
                                                disabled={isFetching}
                                                getRowKey={(c) => c.id}
                                                multiExpand
                                                onExpand={handleOnExpand}
                                                renderRow={(c: Client, isExpanded, onToggle, i) => {
                                            const highlight = selectedClients.some(client => client.id === c.id) && !selectedForm ? 'highlight' : null;
                                            return (
                                                <tr className={getclassNames(`table-row ${highlight}`, (i % 2 === 0 && 'even-row') || '')}>
                                                    {showCheckbox && (
                                                        <td>
                                                            <Checkbox
                                                                id={`select-client-${c.id}`}
                                                                checked={selectedClients.some(client => client.id === c.id)}
                                                                containerClassName="unboxed margin-top-0"
                                                                onChange={checked => {
                                                                    if (checked) {
                                                                        setSelectedClients([...selectedClients, { id: c.id, assignedTo: c.assignedTo, isAssigned: c.isAssigned }]);
                                                                    } else {
                                                                        setSelectedClients(selectedClients.filter(client => client.id !== c.id));
                                                                    }
                                                                }}
                                                            />
                                                        </td>
                                                    )}
                                                    <td>
                                                        <IconButton
                                                            className="expand-chevron small flat"
                                                            id={`expand-line-${i}`}
                                                            icon={<i className={isExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />}
                                                            onClick={onToggle}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Link to={`/participants/${c.id}/profile-demographics`} target="_blank">
                                                            {c.lastName}, {c.firstName}
                                                        </Link>
                                                    </td>
                                                    {householdIdentifiersEnabled && <td>{getDisplayHouseholdIds(c)}</td>}
                                                    <td>{c.dateOfBirth && format(parseJSONDateWithoutTime(c.dateOfBirth), 'MM/dd/yyyy')}</td>
                                                    {showPhone && <td>{c.phoneNumber}</td>}
                                                    <td>{(c.updated && format(new Date(c.updated), 'Pp')) || (c.created && format(new Date(c.created), 'Pp'))}</td>
                                                    {assignedUserEnabled && (
                                                        <td>{(c.assignedTo && c.assignedTo.firstName && c.assignedTo.lastName) ? `${c.assignedTo.firstName} ${c.assignedTo.lastName}` : ""}</td>
                                                    )}
                                                    {showTrackData && (
                                                        <td>
                                                            {(c.recentFormId1 || c.recentFormId2 || c.recentFormId3) &&
                                                                <Dropdown id={`recent-forms-${c.id}`} options={getRecentForms(c)} optionSize="large" />}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                                }}
                                                renderExpandedContent={(c: Client) => (
                                            <div className="row padding-3">
                                                <div className="column">
                                                    {loadingClients.includes(c.id) && <Loader />}
                                                    {!loadingClients.includes(c.id) && getLoadedClient(c.id) && <>
                                                        <div className="row margin-top-2">
                                                            <div className="column-medium-3">
                                                                <label>Name</label>
                                                                <div>{getLoadedClient(c.id)!.firstName} {getLoadedClient(c.id)!.middleName} {getLoadedClient(c.id)!.lastName}</div>
                                                            </div>
                                                            {householdIdentifiersEnabled && <div className="column-medium-3">
                                                                <label>Household ID</label>
                                                                <div>{getDisplayHouseholdIds(getLoadedClient(c.id))}</div>
                                                            </div>}
                                                            <div className="column-medium-3">
                                                                <label>Date of Birth</label>
                                                                <div>{formatOptionalJSONDateWithoutTime(getLoadedClient(c.id)!.dateOfBirth, 'MM/dd/yyyy')}</div>
                                                            </div>
                                                            {showGender && <div className="column-medium-3">
                                                                <label>Gender</label>
                                                                <div>{getDisplayGenderName(getLoadedClient(c.id)!.genderName, organization.value)}</div>
                                                            </div>}
                                                            {showEthnicity && <div className="column-medium-3">
                                                                <label>Ethnicity</label>
                                                                <div>{getLoadedClient(c.id)!.ethnicityName}</div>
                                                            </div>}
                                                        </div>
                                                        {showRace && <div className="row margin-top-2">
                                                            <div className="column">
                                                                <label>{raceFieldLabel}</label>
                                                                <div>{getDisplayRaceNames({
                                                                    ethnicityName: getLoadedClient(c.id)!.ethnicityName,
                                                                    races: getLoadedClient(c.id)!.races
                                                                }, organization.value).join(', ')}</div>
                                                            </div>
                                                        </div>}
                                                        {loadingClientForms.includes(c) && (
                                                            <div className="row justify-content-center margin-top-2">
                                                                <div className="column-auto"><Loader /></div>
                                                            </div>
                                                        )}
                                                        {!loadingClientForms.includes(c) && clientForms.some(cf => cf.clientId === c.id) && (
                                                            <div className="search-results-additional-information">
                                                                <FormViewer instance={getClientForm(c.id)} />
                                                            </div>
                                                        )}
                                                        {getDisplayHouseholdMembers(getLoadedClient(c.id)).length > 0 &&
                                                            <h3>Household Members{householdIdentifiersEnabled && getDisplayHouseholdIds(getLoadedClient(c.id)) ? ` - Household ID: ${getDisplayHouseholdIds(getLoadedClient(c.id))}` : ''}</h3>}
                                                        {getDisplayHouseholdMembers(getLoadedClient(c.id)).map((hhm: HouseholdMember, hhmIndex: number) => {
                                                            const linkedProfileHref = hhm.linkedClientId ? `/participants/${hhm.linkedClientId}/profile-demographics` : '';
                                                            const linkedEditHref = hhm.linkedClientId ? (canEditParticipants ? `/participants/${hhm.linkedClientId}/edit` : linkedProfileHref) : '';

                                                            return <Fragment key={`client-household-member-${hhm.id}`}>
                                                                <hr />
                                                                <div className="row">
                                                                    <div className="column">
                                                                        <div className="row">
                                                                            <div className="column-medium-3">
                                                                                <label>Name</label>
                                                                                <div>{hhm.firstName} {hhm.middleName} {hhm.lastName}</div>
                                                                                {linkedHouseholdParticipantsEnabled && hhm.linkedClientId && <div id={`search-linked-household-indicator-${c.id}-${hhmIndex}`} className="text-bold margin-top-1">Linked Participant</div>}
                                                                            </div>
                                                                            <div className="column-medium-3">
                                                                                <label>Date of Birth</label>
                                                                                <div>{hhm.dateOfBirth && format(parseJSONDateWithoutTime(hhm.dateOfBirth), 'MM/dd/yyyy')}</div>
                                                                            </div>
                                                                            {showGender && <div className="column-medium-3">
                                                                                <label>Gender</label>
                                                                                <div>{getDisplayGenderName(hhm.genderName, organization.value)}</div>
                                                                            </div>}
                                                                            {showEthnicity && <div className="column-medium-3">
                                                                                <label>Ethnicity</label>
                                                                                <div>{hhm.ethnicityName}</div>
                                                                            </div>}
                                                                        </div>
                                                                        {showRace && <div className="row">
                                                                            <div className="column">
                                                                                <label>{raceFieldLabel}</label>
                                                                                <div>{getDisplayRaceNames({ ethnicityName: hhm.ethnicityName, races: hhm.races }, organization.value).join(', ')}</div>
                                                                            </div>
                                                                        </div>}
                                                                        {linkedHouseholdParticipantsEnabled && hhm.linkedClientId && <div className="row margin-top-1">
                                                                            <div className="column-auto">
                                                                                <Link id={`search-linked-household-view-profile-${c.id}-${hhmIndex}`} to={linkedProfileHref} target="_blank">View Profile</Link>
                                                                            </div>
                                                                            <div className="column-auto">
                                                                                <Link id={`search-linked-household-edit-profile-${c.id}-${hhmIndex}`} to={linkedEditHref} target="_blank">Edit Profile</Link>
                                                                            </div>
                                                                        </div>}
                                                                    </div>
                                                                </div>
                                                            </Fragment>
                                                        })}
                                                    </>}
                                                </div>
                                            </div>
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {selectedForm && <>
                                    {loadingSelectedForm && <Loader />}
                                    {!loadingSelectedForm && loadedForm &&
                                        <div className="column border-start padding-start-5 search-results-side-panel">
                                            <div className="row justify-content-between">
                                                <div className="column-auto">
                                                    <h3>{selectedForm.formName}</h3>
                                                    <h4>{selectedForm.client.lastName}, {selectedForm.client.firstName}</h4>
                                                </div>
                                                <div className="column-auto">
                                                    <IconButton id="close-form-button" icon={<i className="ri-close-line"></i>} className="flat small" onClick={closeForm} />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="column">
                                                    <form onSubmit={handleSubmitForm}>
                                                        <ApiErrorMessages error={apiError} />
                                                        <div className="column">
                                                            <FormEditor disabled={false} fields={loadedFormFields} onChange={handleOnFieldsChange} columnNumber={2} />
                                                        </div>
                                                        <div className="row margin-top-3 justify-content-between">
                                                            <div className="column-auto">
                                                                <Button id="reset-button" className="secondary" text="Reset" disabled={submitting} onClick={handleFormReset} />
                                                            </div>
                                                            <div className="column-auto">
                                                                <Button id="submit-button" text="Submit" disabled={submitting || loadedFormFields.some(f => f.files && f.files.some(ff => ff.status !== UPLOADED))} loading={submitting} type="submit" />
                                                            </div>
                                                        </div>
                                                    </form>
                                                </div>
                                            </div>
                                        </div>}
                                </>}
                            </div>
                        </div>
                    </div>
                    <Modal id="assign-to-user-modal" title="Assign to User" className="small padding-horizontal-5" visible={isUserAssignmentModalOpen} onClose={() => setIsUserAssignmentModalOpen(false)}>
                        {isUserAssignmentModalOpen && <div className="row">
                            <div className="column">
                                <Select id="users-list" label="Select a user" value={selectedUser} onChange={(user) => setSelectedUser(user)}>
                                    <option value="">-- Select a user --</option>
                                    {isUsersLoading && <option>Loading users...</option>}
                                    {!isUsersLoading && assignableUsers.map(user => (
                                        <option key={`user-option-${user.id}`} value={user.id} selected={user.id === selectedUser}>
                                            {user.firstName} {user.lastName}
                                        </option>
                                    ))}
                                </Select>
                                <p className="text-larger-x1-2">Selected participants:</p>
                                <ul>
                                    {selectedClients.map(client => {
                                        const foundClient = clients.find(c => c.id === client.id);
                                        return <li key={`selected-client-${client.id}`}>{foundClient?.firstName} {foundClient?.lastName} - {foundClient?.dateOfBirth && format(parseJSONDateWithoutTime(foundClient.dateOfBirth), 'MM/dd/yyyy')}</li>;
                                    })}
                                </ul>
                                <div className="row justify-content-between margin-top-4">
                                    <div className="column-auto">
                                        <Button id="cancel-assign-users" className="secondary" text="Cancel" disabled={isAssigning} onClick={() => setIsUserAssignmentModalOpen(false)} />
                                    </div>
                                    <div className="column-auto">
                                        <Button id="confirm-assign-users" text="Confirm" disabled={!selectedUser || isAssigning} loading={isAssigning} onClick={handleBulkAssignClientsToUsers} />
                                    </div>
                                </div>
                            </div>
                        </div>}
                    </Modal>
                    <Modal id="unassign-from-user-modal" title="Unassign from User" className="small padding-horizontal-5" visible={isUserUnassignmentModalOpen} onClose={() => setIsUserUnassignmentModalOpen(false)}>
                        {isUserUnassignmentModalOpen && <div className="row">
                            <div className="column">
                                <div className="padding-bottom-2">
                                    <p>Are you sure you want to unassign from the selected user(s)?</p>
                                    <ul>
                                        {selectedClients.map(client => {
                                            const foundClient = clients.find(c => c.id === client.id);
                                            return (
                                                <li key={`selected-client-${client.id}`}>
                                                    {foundClient?.lastName}{", "}
                                                    {foundClient?.firstName}{" - "}
                                                    <strong>{foundClient?.assignedTo?.firstName} {foundClient?.assignedTo?.lastName}</strong>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                                <div className="row justify-content-between margin-top-4">
                                    <div className="column-auto">
                                        <Button id="cancel-assign-users" className="secondary" text="No, Cancel" disabled={isUnassigning} onClick={() => setIsUserUnassignmentModalOpen(false)} />
                                    </div>
                                    <div className="column-auto">
                                        <Button id="confirm-assign-users" text="Yes, Unassign" className="button warning" loading={isUnassigning} disabled={isUnassigning} onClick={handleBulkUnassignClientsFromUsers} />
                                    </div>
                                </div>
                            </div>
                        </div>}
                    </Modal>
                </>
            }
            {clients && clients.length === 0 && (
                <div className="row justify-content-center">
                    <div className="column-auto">
                        <p>No Matches For Your Search</p>
                        <div className="row justify-content-center">
                            <div className="column-auto">
                                <Button id="create-participant" href="/participants/add" text="Add Participant" />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const BulkActionAssignment = ({
    selectedClients,
    setAssignmentModelOpen,
    setUnassignmentModelOpen
}: {
    selectedClients: SelectedClients[];
    setAssignmentModelOpen: (open: boolean) => void;
    setUnassignmentModelOpen: (open: boolean) => void;
}) => {
    const hasAssigned = selectedClients.some(c => c.isAssigned);
    const hasUnassigned = selectedClients.some(c => !c.isAssigned);
    const mixedSelection = hasAssigned && hasUnassigned;

    return (
        <>
            <div className="column-auto">
                {selectedClients.length !== 0 && <>
                    {mixedSelection ? (
                        <p className="margin-y-0 margin-start-2">No actions available</p>
                    ) : hasAssigned ? (
                        <Button className="secondary" id="unassign-from-users" text="Unassign from user" onClick={() => setUnassignmentModelOpen(true)} />
                    ) : (
                        <Button className="secondary" id="assign-to-users" text="Assign to user" onClick={() => setAssignmentModelOpen(true)} />
                    )}
                </>}
            </div>
            <div className="column-auto">
                <p className="margin-y-0">
                    {selectedClients.length > 0 ? `${selectedClients.length} Selected` : '0 selected'}
                </p>
            </div>
        </>
    );
};

const SearchResultsTable = () => manageProgramsV2Enabled
    ? <SearchResultsTableV2 />
    : <SearchResultsTableV1 />;

export default SearchResultsTable;
